<template>
  <div>
    <!-- WhatsApp Readiness Check -->
    <div
      v-if="
        !whatsappState.isReady &&
        !whatsappState.isCheckingReadiness &&
        !whatsappState.isCheckingTemplateStatus
      "
      class="relative mt-3 overflow-hidden rounded-lg p-6"
      style="border: 1px solid #039855; background-color: #ecfdf3"
    >
      <!-- Background Pattern -->
      <div
        class="absolute inset-0"
        style="
          background-image: url('/src/assets/whatsapp-background-pattern.png');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        "
      ></div>

      <!-- Content -->
      <div class="relative z-10 flex items-center justify-between">
        <div class="flex-1">
          <h3 class="mb-2 text-lg font-semibold text-gray-900">
            {{ t('eventNotification.whatsapp.setupTitle') }}
          </h3>
          <p class="text-sm leading-relaxed text-gray-600">
            {{ t('eventNotification.whatsapp.setupDescription') }}
          </p>
        </div>
        <div class="ml-6 flex flex-shrink-0 items-center">
          <UIButton
            type="secondary"
            size="medium"
            class="flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-gray-700 shadow-sm hover:bg-gray-50"
            @click="openWhatsAppSetup"
          >
            <svg
              class="mr-3 h-5 w-5 text-green-500"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.386"
              />
            </svg>
            <span class="font-bold">{{
              t('eventNotification.whatsapp.connectWhatsApp')
            }}</span>
          </UIButton>
        </div>
      </div>
    </div>

    <!-- Loading state for readiness check -->
    <div
      v-if="whatsappState.isCheckingReadiness"
      class="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4"
    >
      <div class="text-sm text-blue-700">
        {{ t('eventNotification.whatsapp.checkingStatus') }}
      </div>
    </div>

    <!-- Loading state for template status check -->
    <div
      v-if="whatsappState.isCheckingTemplateStatus"
      class="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4"
    >
      <div class="text-sm text-blue-700">
        {{ t('eventNotification.whatsapp.checkingTemplateStatus') }}
      </div>
    </div>

    <!-- WhatsApp Setup Banner (shown when ready but no template access) -->
    <div
      v-if="
        whatsappState.isReady &&
        !whatsappState.hasTemplateAccess &&
        !whatsappState.isCheckingTemplateStatus
      "
      class="relative mt-3 overflow-hidden rounded-lg p-6"
      style="border: 1px solid #039855; background-color: #ecfdf3"
    >
      <!-- Background Pattern -->
      <div
        class="absolute inset-0"
        style="
          background-image: url('/src/assets/whatsapp-background-pattern.png');
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        "
      ></div>

      <!-- Content -->
      <div class="relative z-10 flex items-center justify-between">
        <div class="flex-1">
          <h3 class="mb-2 text-lg font-semibold text-gray-900">
            {{ t('eventNotification.whatsapp.setupTemplateTitle') }}
          </h3>
          <p class="text-sm leading-relaxed text-gray-600">
            {{ t('eventNotification.whatsapp.setupTemplateDescription') }}
          </p>
        </div>
        <div class="ml-6 flex flex-shrink-0 items-center">
          <UIButton
            type="secondary"
            size="medium"
            class="flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-gray-700 shadow-sm hover:bg-gray-50"
            @click="openWhatsAppSetup"
          >
            <svg
              class="mr-3 h-5 w-5 text-green-500"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.386"
              />
            </svg>
            <span class="font-bold">{{
              t('eventNotification.whatsapp.connectWhatsApp')
            }}</span>
          </UIButton>
        </div>
      </div>
    </div>

    <!-- WhatsApp Toggle and Configuration (shown when ready and has template access) -->
    <div v-if="whatsappState.isReady && whatsappState.hasTemplateAccess">
      <div class="mt-3 flex space-x-3 py-2">
        <UISwitch
          id="whatsapp-notification-switch"
          v-model:value="isNotificationsEnabled"
          :aria-label="t('alt_text.whatsapp_notification_switch')"
          :title="t('alt_text.whatsapp_notification_switch')"
          class="w-1/3"
          size="medium"
          @update:value="
            value => {
              toggleNotification(value)
            }
          "
        />
        <div class="items-center text-sm font-medium text-gray-700">
          {{
            isNotificationsEnabled
              ? t('eventNotification.enabled')
              : t('eventNotification.disabled')
          }}
        </div>
      </div>
      <div class="my-2 text-sm font-medium text-gray-500">
        {{ t('eventNotification.whatsapp.notificationLabel') }}
      </div>

      <!-- Render all WhatsApp templates of NotificationType - confirmation,reminder and others -->
      <UITooltip :trigger="!isNotificationsEnabled ? 'hover' : 'manual'">
        <template #trigger>
          <div :class="{ 'disabled-div': !isNotificationsEnabled }">
            <div
              v-for="(whatsappNotification, i) in state.whatsappNotifications"
              :key="'whatsapp-notification-' + i"
            >
              <!-- Render a single WhatsApp configurator window -->
              <WhatsAppTemplateConfigurator
                ref="whatsappTemplateConfigurator"
                :index="i"
                :class="{
                  'rounded-t-lg border': i === 0,
                  'rounded-b-lg border':
                    i === state.whatsappNotifications.length - 1,
                }"
                :notification="whatsappNotification"
                :is-notifications-enabled="isNotificationsEnabled"
                :is-refreshing-templates="whatsappState.isRefreshingTemplates"
              />
            </div>
          </div>
        </template>
        <div>{{ t('eventNotification.tooltip') }}</div>
      </UITooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { EventNotificationService } from '@/class/services/EventNotificationService'
import WhatsappService, {
  WhatsappTemplateService,
} from '@/class/services/WhatsappService'
import { useNotify } from '@/components/composition/notification'
import AppState, {
  getWhatsAppDefaultTemplates,
  getWhatsAppTemplates,
} from '@/states/app'
import {
  getDefaultWhatsAppNotifications,
  getEventNotificationState,
  eventNotificationState as state,
  updateNotificationState as updateGlobalNotificationState,
} from '@/states/EventNotificationState'
import { AltType, NotificationChannel } from '@/types/notification'
import { getDefaultWhatsAppTemplateLabel } from '@/utils/default-notification-template'
import { UIButton, UISwitch, UITooltip } from '@gohighlevel/ghl-ui'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import WhatsAppTemplateConfigurator from './WhatsAppTemplateConfigurator.vue'

const { t } = useI18n()
const { sendNotification } = useNotify()

// Reactive state
const isNotificationsEnabled = ref(true)
const whatsappTemplateConfigurator = ref(null) as any
const whatsappState = ref({
  isReady: false,
  isCheckingReadiness: false,
  hasTemplateAccess: false,
  isCheckingTemplateStatus: false,
  isRefreshingTemplates: false,
})

// Template refresh debouncing
let refreshTimeout: ReturnType<typeof setTimeout> | null = null
let isRefreshInProgress = false

defineExpose({ validateAll })
const emit = defineEmits(['update-notification'])

onMounted(async () => {
  await checkWhatsAppReadiness()
  if (whatsappState.value.isReady) {
    await checkWhatsAppTemplateStatus()
    if (whatsappState.value.hasTemplateAccess) {
      isNotificationsEnabled.value = getEventNotificationState('whatsapp')
      // Only load templates if WhatsApp notifications are enabled
      if (isNotificationsEnabled.value) {
        loadWhatsAppTemplatesInBackground()
      }
    }
  }
})

// Cleanup on component unmount
onUnmounted(() => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
    refreshTimeout = null
  }
})

// Watch for template creation/editing completion and refresh templates globally with debouncing
watch(
  () => AppState.whatsappNotificationTemplate.isUpdated,
  newValue => {
    if (newValue && whatsappState.value.hasTemplateAccess) {
      // Debounce template refresh to prevent multiple rapid calls
      debouncedRefreshWhatsAppTemplates()

      // Reset the flag to ensure future template updates trigger the watcher
      nextTick(() => {
        AppState.whatsappNotificationTemplate.isUpdated = false
        AppState.whatsappNotificationTemplate.templateId = ''
      })
    }
  }
)

/**
 * Check WhatsApp account readiness
 */
async function checkWhatsAppReadiness() {
  whatsappState.value.isCheckingReadiness = true

  try {
    const data = await new WhatsappService(null).checkWhatsAppReadiness()
    whatsappState.value.isReady = data.isReady || false
  } catch (error) {
    console.error('Error checking WhatsApp readiness:', error)
    whatsappState.value.isReady = false
    sendNotification({
      type: 'error',
      title: t('eventNotification.whatsapp.statusCheckError'),
      icon: 'AlertCircleIcon',
    })
  } finally {
    whatsappState.value.isCheckingReadiness = false
  }
}

/**
 * Check WhatsApp template status
 */
async function checkWhatsAppTemplateStatus() {
  whatsappState.value.isCheckingTemplateStatus = true

  try {
    const data = await WhatsappTemplateService.getTemplateStatusForLocation()
    whatsappState.value.hasTemplateAccess = data.success || false
  } catch (error) {
    console.error('Error checking WhatsApp template status:', error)
    whatsappState.value.hasTemplateAccess = false
    sendNotification({
      type: 'error',
      title: t('eventNotification.whatsapp.templateStatusCheckError'),
      icon: 'AlertCircleIcon',
    })
  } finally {
    whatsappState.value.isCheckingTemplateStatus = false
  }
}

/**
 * Load WhatsApp templates from API (both user and default templates combined for dropdown)
 */
async function loadWhatsAppTemplatesInBackground() {
  try {
    // Load combined templates (user + default) for dropdown
    // Also load default templates separately for auto-selection logic
    const [combinedTemplates, defaultTemplates] = await Promise.all([
      getWhatsAppTemplates(true), // Combined user + default templates for dropdown
      getWhatsAppDefaultTemplates(true), // Default templates for auto-selection logic
    ])

    // After loading templates, resolve template IDs for existing WhatsApp notifications
    if (state.whatsappNotifications?.length) {
      await resolveWhatsAppDefaultTemplates()
    }
  } catch (error) {
    console.error('Error loading WhatsApp templates:', error)
    sendNotification({
      type: 'error',
      title: t('eventNotification.whatsapp.loadTemplatesError'),
      icon: 'AlertCircleIcon',
    })
  }
}

/**
 * Debounced template refresh to prevent multiple concurrent API calls
 */
function debouncedRefreshWhatsAppTemplates() {
  // Clear any existing timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout)
  }

  // If a refresh is already in progress, don't start another one
  if (isRefreshInProgress) {
    return
  }

  // Set up debounced refresh with 300ms delay
  refreshTimeout = setTimeout(() => {
    refreshWhatsAppTemplatesGlobally()
  }, 300)
}

/**
 * Refresh WhatsApp templates globally for all child components with deduplication
 */
async function refreshWhatsAppTemplatesGlobally() {
  // Prevent multiple concurrent refreshes
  if (isRefreshInProgress) {
    return
  }

  try {
    isRefreshInProgress = true
    whatsappState.value.isRefreshingTemplates = true

    // Force refresh templates from API
    await getWhatsAppTemplates(true)

    // The child components will automatically update via the AppState.whatsappTemplates watcher
    // No need to manually update each component

    sendNotification({
      type: 'success',
      title: t(
        'eventNotification.whatsapp.templatesRefreshed',
        'Templates refreshed successfully'
      ),
      icon: 'CheckCircleIcon',
    })
  } catch (error) {
    console.error('Error refreshing WhatsApp templates globally:', error)
    sendNotification({
      type: 'error',
      title: t(
        'eventNotification.whatsapp.templatesRefreshError',
        'Failed to refresh templates'
      ),
      icon: 'AlertCircleIcon',
    })
  } finally {
    isRefreshInProgress = false
    whatsappState.value.isRefreshingTemplates = false

    // Clear the timeout reference
    if (refreshTimeout) {
      clearTimeout(refreshTimeout)
      refreshTimeout = null
    }
  }
}

/**
 * Resolve default template IDs for WhatsApp notifications
 */
async function resolveWhatsAppDefaultTemplates() {
  if (!state.whatsappNotifications?.length) return

  try {
    const notificationsWithTemplates = await Promise.all(
      state.whatsappNotifications.map(notification =>
        resolveDefaultTemplateForNotification(notification)
      )
    )

    state.whatsappNotifications = notificationsWithTemplates
  } catch (error) {
    console.warn(t('eventNotification.whatsapp.templatesResolveError'), error)
  }
}

/**
 * Resolve default template for a single notification
 */
async function resolveDefaultTemplateForNotification(notification) {
  // Skip if already has template
  if (notification.templateId) {
    return notification
  }

  try {
    const defaultNotifications = await getDefaultWhatsAppNotifications({
      altId: state.calendarId,
      altType: AltType.CALENDAR,
      notificationType: notification.notificationType,
      channel: NotificationChannel.WHATSAPP,
      receiverType: notification.receiverType,
    })

    const matchingDefault = defaultNotifications?.[0]?.templateId
    return matchingDefault
      ? { ...notification, templateId: matchingDefault }
      : notification
  } catch (error) {
    console.warn(
      `Failed to resolve template for ${notification.receiverType}:`,
      error
    )
    return notification
  }
}

/**
 * Open WhatsApp setup guide or configuration
 */
function openWhatsAppSetup() {
  const locationId = AppState.locationId
  if (locationId) {
    // Use parent app navigation pattern like other external page redirects
    AppState.parentApp?.then(parent => {
      parent.emit('go-to-whatsapp-settings', {
        locationId: locationId,
      })
    })
  } else {
    console.error(t('eventNotification.whatsapp.locationNotFound'))
    sendNotification({
      type: 'error',
      title: t('eventNotification.whatsapp.settingsRedirectError'),
      icon: 'AlertCircleIcon',
    })
  }
}

// Helper function to get default template ID for a notification type and receiver type
async function getDefaultTemplateIdForNotification(
  notificationType: string,
  receiverType: string
): Promise<string | null> {
  try {
    // Get the hardcoded template name
    const defaultTemplateName = getDefaultWhatsAppTemplateLabel(
      notificationType,
      receiverType
    )
    if (!defaultTemplateName) {
      return null
    }

    const defaultTemplates = await getWhatsAppDefaultTemplates()

    if (!defaultTemplates || defaultTemplates.length === 0) {
      return null
    }

    // Find template ID by matching the name
    const matchingTemplate = defaultTemplates.find(template => {
      const templateName = template.templateName || template.name
      return templateName === defaultTemplateName
    })

    if (matchingTemplate) {
      const templateId = matchingTemplate.templateId || matchingTemplate.id
      return templateId
    } else {
      return null
    }
  } catch (error) {
    console.error('[WhatsApp Toggle] Error getting default template ID:', error)
    return null
  }
}

/**
 * Enable/disable all WhatsApp notifications
 */
async function toggleNotification(value: boolean) {
  const notifications = state.whatsappNotifications
  const payload: any[] = []

  // Process each notification and auto-populate default template ID if enabling
  for (const notification of notifications) {
    const updatedNotification: any = {
      ...notification,
      deleted: !value,
    }

    // If enabling notification and it doesn't have a templateId, auto-populate default template ID
    if (value && !notification.templateId) {
      const defaultTemplateId = await getDefaultTemplateIdForNotification(
        notification.notificationType,
        notification.receiverType
      )
      if (defaultTemplateId) {
        updatedNotification.templateId = defaultTemplateId
      }
    }

    payload.push(updatedNotification)
  }

  await updateNotificationState({
    enableState: value,
    notifications: payload,
    onSuccess: () => {
      isNotificationsEnabled.value = value
      handleTemplateLoading(value)
      handleOuterNotification(value, payload[0]?.notificationType)
      showSuccessMessage(value)
    },
    onError: () => {
      sendNotification({
        type: 'error',
        title: t('eventNotification.whatsapp.updateNotificationError'),
        icon: 'AlertCircleIcon',
      })
    },
  })
}

/**
 * Handle template loading based on notification state
 */
function handleTemplateLoading(enabled: boolean) {
  if (enabled) {
    loadWhatsAppTemplatesInBackground()
  }
}

/**
 * Show success message for notification toggle
 */
function showSuccessMessage(enabled: boolean) {
  sendNotification({
    type: 'success',
    title: enabled
      ? t('eventNotification.whatsapp.notificationsEnabled')
      : t('eventNotification.whatsapp.notificationsDisabled'),
    icon: 'CheckCircleIcon',
  })
}

/**
 * Generic notification state updater (could be extracted to a composable)
 */
async function updateNotificationState({
  enableState,
  notifications,
  onSuccess,
  onError,
}) {
  state.disableSave = true

  try {
    const payloadToUpdate = notifications.filter(n => n._id)
    const payloadToCreate = notifications
      .filter(n => !n._id)
      .map(n => {
        const { _id, ...notification } = n
        return notification
      })

    const updatePromises: Promise<void>[] = []

    if (payloadToUpdate.length) {
      updatePromises.push(
        EventNotificationService.updateEventNotifications(payloadToUpdate).then(
          ({ data }) => updateGlobalNotificationState(data)
        )
      )
    }

    if (payloadToCreate.length) {
      updatePromises.push(
        EventNotificationService.createEventNotifications(payloadToCreate).then(
          ({ data }) => updateGlobalNotificationState(data)
        )
      )
    }

    if (updatePromises.length) {
      await Promise.all(updatePromises)
    }

    onSuccess?.()
  } catch (error) {
    console.error(t('eventNotification.whatsapp.updateError'), error)
    onError?.(error)
  } finally {
    state.disableSave = false
  }
}

function handleOuterNotification(value, type) {
  emit('update-notification', {
    channel: 'whatsapp',
    type: type,
    status: value,
  })
}

/**
 * Validate all WhatsApp template configurators
 */
function validateAll() {
  const configurators = whatsappTemplateConfigurator.value
  if (!configurators?.length) {
    return Promise.resolve()
  }

  const validationPromises = configurators
    .filter(config => config?.validateForm)
    .map(config => config.validateForm())

  return Promise.all(validationPromises)
}
</script>

<style scoped>
.disabled-div {
  position: relative;
  opacity: 0.5;
}

.disabled-div input,
.disabled-div textarea,
.disabled-div button,
.disabled-div select {
  pointer-events: none;
  user-select: none;
}

.disabled-div .ui-checkbox,
.disabled-div .ui-switch {
  pointer-events: none;
}
</style>
